import { Component, inject, OnDestroy, computed, signal, ElementRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, switchMap, Subject, takeUntil } from 'rxjs';
import { MoratoriumService, MoratoriumRequest, MoratoriumResponse, MoratoriumItem } from '../services/moratorium.service';
import { TableCardLayoutComponent } from '../shared/table-card-layout/table-card-layout.component';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { globalMessages } from '../shared/global-messages';

interface ExtendedMoratoriumItem extends MoratoriumItem {
    formattedStartDate: string;
    formattedEndDate: string;
    formattedUpdatedAt: string;
}

@Component({
    selector: 'app-moratorium',
    imports: [CommonModule, TableCardLayoutComponent, ...PSIC_COMPONENTS],
    templateUrl: './moratorium.component.html',
    styleUrl: './moratorium.component.scss',
})
export class MoratoriumComponent implements OnDestroy {
    private store = inject(Store);
    private moratoriumService = inject(MoratoriumService);
    private el = inject(ElementRef);

    private policySignal = this.store.selectSignal((state: any) => state.policy);
    private destroy$ = new Subject<void>();

    private moratoriumPayload = computed(() => {
        const policyState = this.policySignal();
        if (!policyState?.data || !policyState.policyNumber) return null;

        const policyData = policyState.data[policyState.policyNumber];
        const generalInfo = policyData?.policySnapshot?.policyGeneralInformation;
        if (!generalInfo) return null;

        const effectiveDate = generalInfo.effectiveDate?.value;
        const expirationDate = generalInfo.expirationDate?.value;
        const address = generalInfo.propertyFullAddress?.value;
        if (!effectiveDate || !expirationDate || !address) return null;

        const parts = address.split(',');
        const lastPart = parts[parts.length - 1]?.trim() || '';
        const zipMatch = lastPart.match(/\b\d{5}(?:-\d{4})?\b/);
        const zipCode = zipMatch ? zipMatch[0] : '';
        if (!zipCode) return null;

        return {
            policyNumber: policyState.policyNumber,
            policyTerm: policyState.policyTerm,
            zipCode,
            startDate: this.formatDateForAPI(effectiveDate),
            endDate: this.formatDateForAPI(expirationDate)
        };
    });

    moratoriums = signal<ExtendedMoratoriumItem[]>([]);
    allMoratoriums = signal<ExtendedMoratoriumItem[]>([]);
    showAll = signal<boolean>(false);
    apiError: string = '';
    isLoaded = signal<boolean>(false);
    failed = signal<boolean>(false);

    globalMessages = globalMessages;
    selectedRow: number | null = null;

    constructor() {
        toObservable(this.moratoriumPayload).pipe(
            distinctUntilChanged((prev, curr) =>
                prev?.policyNumber === curr?.policyNumber &&
                prev?.policyTerm === curr?.policyTerm &&
                prev?.zipCode === curr?.zipCode &&
                prev?.startDate === curr?.startDate &&
                prev?.endDate === curr?.endDate
            ),
            switchMap(payload => {
                if (!payload) return [];
                this.isLoaded.set(true);
                this.failed.set(false);
                const { zipCode, startDate, endDate } = payload;
                return this.moratoriumService.verifyMoratorium({ zipCode, startDate, endDate });
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (response: MoratoriumResponse) => {
                const transformed = response.moratoriums.map(item => ({
                    ...item,
                    formattedStartDate: this.formatDisplayDate(item.startDate),
                    formattedEndDate: this.formatDisplayDate(item.endDate),
                    formattedUpdatedAt: this.formatUpdatedAt(item.updatedAt)
                }));
                this.allMoratoriums.set(transformed);
                this.showAll.set(false);
                this.moratoriums.set(transformed.slice(0, 6));
                this.isLoaded.set(false);
            },
            error: (error: any) => {
                this.apiError = error.message || 'Failed to verify moratorium status';
                this.isLoaded.set(false);
                this.failed.set(true);
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private formatDateForAPI(dateString: string): string {
        if (!dateString) return '';
        // Already YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
        // MM/DD/YYYY
        if (dateString.includes('/')) {
            const [m, d, y] = dateString.split('/');
            if (m && d && y) return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
        return '';
    }

    private formatDisplayDate(dateString: string): string {
        if (!dateString) return '';
        try {
            // Parse YYYY-MM-DD directly to avoid UTC-to-local timezone shift
            const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                const [, yyyy, mm, dd] = match;
                return `${mm}/${dd}/${yyyy}`;
            }
            return dateString;
        } catch {
            return dateString;
        }
    }

    // Parses "yyyyMMdd-HHmmss-SSS tz" e.g. "20260717-021812-854 PDT"
    // Output: "07-17-2026 | 02:18 AM"
    private formatUpdatedAt(updatedAt: string): string {
        if (!updatedAt) return '';
        try {
            const match = updatedAt.match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/);
            if (!match) return updatedAt;
            const [, yyyy, MM, dd, HH, mm] = match;
            const date = new Date(`${yyyy}-${MM}-${dd}T${HH}:${mm}:00`);
            const datePart = date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
            const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
            return `${datePart} | ${timePart}`;
        } catch {
            return updatedAt;
        }
    }

    verifyMoratorium(): void {
        const payload = this.moratoriumPayload();
        if (!payload) return;
        this.isLoaded.set(true);
        this.failed.set(false);
        const { zipCode, startDate, endDate } = payload;
        this.moratoriumService.verifyMoratorium({ zipCode, startDate, endDate }).subscribe({
            next: (response: MoratoriumResponse) => {
                const transformed = response.moratoriums.map(item => ({
                    ...item,
                    formattedStartDate: this.formatDisplayDate(item.startDate),
                    formattedEndDate: this.formatDisplayDate(item.endDate),
                    formattedUpdatedAt: this.formatUpdatedAt(item.updatedAt)
                }));
                this.allMoratoriums.set(transformed);
                this.showAll.set(false);
                this.moratoriums.set(transformed.slice(0, 6));
                this.isLoaded.set(false);
            },
            error: (error: any) => {
                this.apiError = error.message || 'Failed to verify moratorium status';
                this.isLoaded.set(false);
                this.failed.set(true);
            }
        });
    }

    loadMore() {
        this.showAll.set(true);
        this.moratoriums.set(this.allMoratoriums());
    }

    showLess() {
        this.showAll.set(false);
        this.moratoriums.set(this.allMoratoriums().slice(0, 6));
        this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    selectRow(index: number) {
        this.selectedRow = this.selectedRow === index ? null : index;
    }

    reloadMoratoriums() {
        this.verifyMoratorium();
    }
}
